// Biometric app lock for the HIBANE Desk native shell.
//
// SECURITY MODEL — read before changing:
// We deliberately do NOT store the user's password, Supabase refresh token or
// admin session token anywhere for biometrics. The admin session already
// persists (localStorage, validated server-side by `admin-auth-validate` on
// every protected route mount). Face ID / Touch ID / fingerprint therefore acts
// as a *local app lock* in front of that existing session: it decides whether
// the already-authenticated session is allowed to be revealed on this device.
// Nothing new becomes stealable if the device is compromised.
//
// All functions are safe on web — they resolve to "unavailable"/false.

import { isNativeApp } from "./platform";

const PREF_ENABLED_KEY = "hibane_desk_biometric_lock";

export interface BiometricAvailability {
  available: boolean;
  /** Human label for the device's biometry, e.g. "Face ID". */
  label: string;
  /** Reason biometry is unavailable, when `available` is false. */
  reason?: string;
}

const FALLBACK_LABEL = "Biometric unlock";

/** Whether biometric lock can be offered on this device at all. */
export async function checkBiometricAvailability(): Promise<BiometricAvailability> {
  if (!isNativeApp()) {
    return { available: false, label: FALLBACK_LABEL, reason: "Only available in the HIBANE Desk mobile app." };
  }
  try {
    const { BiometricAuth, getBiometryName } = await import("@aparajita/capacitor-biometric-auth");
    const info = await BiometricAuth.checkBiometry();
    const label = getBiometryName(info.biometryType) || FALLBACK_LABEL;
    return {
      available: info.isAvailable,
      label,
      reason: info.isAvailable ? undefined : info.reason || "No biometrics enrolled on this device.",
    };
  } catch (err) {
    console.warn("[native] biometry check failed", err);
    return { available: false, label: FALLBACK_LABEL, reason: "Biometrics unavailable on this device." };
  }
}

/**
 * Prompt for biometric verification.
 * Returns true only on a successful verification.
 */
export async function verifyBiometric(reason = "Unlock HIBANE Desk"): Promise<boolean> {
  if (!isNativeApp()) return false;
  try {
    const { BiometricAuth } = await import("@aparajita/capacitor-biometric-auth");
    await BiometricAuth.authenticate({
      reason,
      cancelTitle: "Cancel",
      iosFallbackTitle: "Use passcode",
      androidTitle: "HIBANE Desk",
      androidSubtitle: reason,
      allowDeviceCredential: true,
    });
    return true;
  } catch (err) {
    // User cancelled or verification failed — both are a plain "not unlocked".
    console.info("[native] biometric verification not completed", (err as Error)?.message);
    return false;
  }
}

/** Read the user's biometric-lock preference for this device. */
export async function isBiometricLockEnabled(): Promise<boolean> {
  if (!isNativeApp()) return false;
  try {
    const { Preferences } = await import("@capacitor/preferences");
    const { value } = await Preferences.get({ key: PREF_ENABLED_KEY });
    return value === "true";
  } catch {
    return false;
  }
}

/** Persist the biometric-lock preference for this device. */
export async function setBiometricLockEnabled(enabled: boolean): Promise<void> {
  if (!isNativeApp()) return;
  try {
    const { Preferences } = await import("@capacitor/preferences");
    await Preferences.set({ key: PREF_ENABLED_KEY, value: enabled ? "true" : "false" });
  } catch (err) {
    console.warn("[native] could not persist biometric preference", err);
  }
}
